/**
 * Employee Ledgers — Loans / Canteen / Colony Bills in one tabbed page.
 *
 * Route: /hr/employee-ledgers
 *
 * Drives F&F auto-fetch. Each tab is a list + create form. Minimal MVP
 * — designed so HR can start entering data immediately, no fancy
 * dashboards yet (Phase 4+ adds reports).
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Card, DatePicker, Drawer, Form, Input, InputNumber, Select, Space,
  Table, Tabs, Tag, Typography, message,
} from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  ledgerApi, LOAN_TYPES, CANTEEN_TRANSACTION_TYPES, COLONY_BILL_STATUSES,
  LOAN_STATUS_COLOR, BILL_STATUS_COLOR, isBondLoanType,
  type CanteenTransaction, type ColonyBill, type EmployeeLoan, type LoanType,
} from '@/features/hr/employeeLedgerApi'
import { employeesApi } from '@/features/hr/api'

const { Title, Text } = Typography

export default function EmployeeLedgersPage() {
  const [empFilter, setEmpFilter] = useState<string | undefined>()

  // Shared employee dropdown options
  const empListQ = useQuery({
    queryKey: ['employees-for-ledger'],
    queryFn: () => employeesApi.list({ page: 1, size: 1000 }),
  })
  const empOptions = (empListQ.data?.items ?? []).map((e) => ({
    label: `${e.emp_code} — ${e.emp_name}`, value: e.id,
  }))

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>Employee Ledgers</Title>
      <Text type="secondary">
        Loans + Canteen transactions + Colony monthly bills. These feed the F&F auto-fetch on separation.
      </Text>

      <Card size="small" style={{ marginTop: 16, marginBottom: 16 }}>
        <Space>
          <Text>Filter by employee:</Text>
          <Select
            allowClear
            showSearch
            style={{ width: 320 }}
            value={empFilter}
            onChange={setEmpFilter}
            placeholder="All employees"
            options={empOptions}
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Space>
      </Card>

      <Tabs
        items={[
          { key: 'loans', label: 'Loans', children: <LoansTab empFilter={empFilter} empOptions={empOptions} /> },
          { key: 'canteen', label: 'Canteen Transactions', children: <CanteenTab empFilter={empFilter} empOptions={empOptions} /> },
          { key: 'colony', label: 'Colony Bills', children: <ColonyTab empFilter={empFilter} empOptions={empOptions} /> },
        ]}
      />
    </div>
  )
}


// ─── LOANS TAB ──────────────────────────────────────────────────

function LoansTab({ empFilter, empOptions }: { empFilter?: string; empOptions: any[] }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()
  const [recoveryOpen, setRecoveryOpen] = useState<EmployeeLoan | null>(null)
  const [recoveryForm] = Form.useForm()
  // Watch the loan_type field so the form can conditionally show bond fields.
  const watchedLoanType = Form.useWatch('loan_type', form) as LoanType | undefined
  const isBondForm = isBondLoanType(watchedLoanType)

  const listQ = useQuery({
    queryKey: ['loans', empFilter],
    queryFn: () => ledgerApi.listLoans({ employee_id: empFilter }),
  })

  const createMut = useMutation({
    mutationFn: (v: any) => ledgerApi.createLoan({
      employee_id: v.employee_id,
      loan_type: v.loan_type,
      principal_amount: v.principal_amount,
      emi_count: v.emi_count,
      sanction_date: dayjs(v.sanction_date).format('YYYY-MM-DD'),
      first_recovery_date: dayjs(v.first_recovery_date).format('YYYY-MM-DD'),
      interest_rate_pct: v.interest_rate_pct ?? 0,
      purpose: v.purpose,
      remarks: v.remarks,
      // Bond fields only sent for bond-type loans
      bond_period_months: isBondLoanType(v.loan_type) ? (v.bond_period_months ?? 12) : undefined,
      bond_start_date: isBondLoanType(v.loan_type) && v.bond_start_date
        ? dayjs(v.bond_start_date).format('YYYY-MM-DD') : undefined,
    }),
    onSuccess: (loan) => {
      message.success(`Loan ${loan.loan_no} sanctioned`)
      setCreateOpen(false); form.resetFields()
      qc.invalidateQueries({ queryKey: ['loans'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const recoveryMut = useMutation({
    mutationFn: (v: any) => ledgerApi.recordLoanRecovery(recoveryOpen!.id, {
      amount: v.amount,
      recovered_date: dayjs(v.recovered_date).format('YYYY-MM-DD'),
      remarks: v.remarks,
    }),
    onSuccess: () => {
      message.success('Recovery recorded')
      setRecoveryOpen(null); recoveryForm.resetFields()
      qc.invalidateQueries({ queryKey: ['loans'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Recovery failed'),
  })

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          New Loan
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>Refresh</Button>
      </Space>

      <Table<EmployeeLoan>
        dataSource={listQ.data ?? []}
        rowKey="id"
        loading={listQ.isLoading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={[
          { title: 'Loan #', dataIndex: 'loan_no', render: (v) => <Text code>{v}</Text>, width: 130 },
          { title: 'Employee', key: 'emp', render: (_, r) =>
            <Text>{r.employee_code}</Text>, width: 100 },
          {
            title: 'Type', key: 'loan_type', width: 180,
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Tag color={isBondLoanType(r.loan_type) ? 'purple' : 'default'}>
                  {r.loan_type.replace(/_/g, ' ')}
                </Tag>
                {isBondLoanType(r.loan_type) && r.bond_period_months && r.bond_start_date && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {(() => {
                      const start = dayjs(r.bond_start_date)
                      const end = start.add(r.bond_period_months, 'month')
                      const now = dayjs()
                      const completed = now.isAfter(end) || now.isSame(end, 'day')
                      const monthsLeft = end.diff(now, 'month')
                      return completed
                        ? '✓ Bond completed'
                        : `Bond: ${monthsLeft >= 0 ? `${monthsLeft}mo left` : 'overdue'}`
                    })()}
                  </Text>
                )}
              </Space>
            ),
          },
          { title: 'Principal', dataIndex: 'principal_amount', render: (v) => `₹${v.toFixed(2)}`, width: 110 },
          { title: 'EMI', key: 'emi', render: (_, r) => `${r.emi_count} × ₹${r.emi_amount.toFixed(2)}`, width: 130 },
          { title: 'Outstanding', dataIndex: 'outstanding_principal',
            render: (v) => <Text strong type={v > 0 ? 'danger' : undefined}>₹{v.toFixed(2)}</Text>, width: 130 },
          { title: 'Status', dataIndex: 'status',
            render: (v: any) => <Tag color={LOAN_STATUS_COLOR[v as keyof typeof LOAN_STATUS_COLOR]}>{v}</Tag>, width: 100 },
          { title: 'Sanctioned', dataIndex: 'sanction_date', render: (v) => dayjs(v).format('DD MMM YY'), width: 110 },
          {
            title: 'Action', key: 'action', width: 130,
            render: (_, r) => r.status === 'ACTIVE'
              ? <Button size="small" onClick={() => setRecoveryOpen(r)}>Record Recovery</Button>
              : null,
          },
        ]}
      />

      <Drawer
        open={createOpen} title="New Loan" width={480}
        onClose={() => setCreateOpen(false)}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="primary" loading={createMut.isPending}
              onClick={() => form.validateFields().then((v) => createMut.mutate(v))}>
              Sanction
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select showSearch options={empOptions}
              filterOption={(i, o) => (o?.label as string ?? '').toLowerCase().includes(i.toLowerCase())} />
          </Form.Item>
          <Form.Item name="loan_type" label="Type" rules={[{ required: true }]}>
            <Select options={LOAN_TYPES.map((t) => ({ label: t.replace(/_/g, ' '), value: t }))} />
          </Form.Item>
          <Form.Item name="principal_amount" label="Principal Amount (₹)" rules={[{ required: true }]}>
            <InputNumber min={1} step={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="emi_count" label="Number of EMIs" rules={[{ required: true }]}>
            <InputNumber min={1} max={120} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sanction_date" label="Sanction Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="first_recovery_date" label="First Recovery Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="interest_rate_pct" label="Interest Rate (% annual)" initialValue={0}>
            <InputNumber min={0} max={50} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          {isBondForm && (
            <>
              <div style={{
                background: '#fff7e6', border: '1px solid #ffe58f',
                padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12,
              }}>
                <Text strong>Bond rule:</Text> if the worker stays the full
                bond period, this loan is automatically waived on separation
                (company absorbs the cost). If they leave early, the full
                outstanding is recovered from F&F.
              </div>
              <Form.Item
                name="bond_period_months"
                label="Bond Period (months)"
                initialValue={12}
                rules={[{ required: true, message: 'Required for bond loans' }]}
                tooltip="Typically 12 months. Trainees often 6, senior hires 18-24."
              >
                <InputNumber min={1} max={60} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="bond_start_date"
                label="Bond Start Date (optional)"
                tooltip="Defaults to Sanction Date. Override only if the bond clock starts later (e.g. after probation)."
              >
                <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
              </Form.Item>
            </>
          )}

          <Form.Item name="purpose" label="Purpose"><Input /></Form.Item>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Drawer>

      {recoveryOpen && (
        <Drawer
          open={!!recoveryOpen} title={`Record Recovery — ${recoveryOpen.loan_no}`} width={420}
          onClose={() => setRecoveryOpen(null)}
          footer={
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={() => setRecoveryOpen(null)}>Cancel</Button>
              <Button type="primary" loading={recoveryMut.isPending}
                onClick={() => recoveryForm.validateFields().then((v) => recoveryMut.mutate(v))}>
                Record
              </Button>
            </Space>
          }
        >
          <Form form={recoveryForm} layout="vertical"
            initialValues={{ amount: recoveryOpen.emi_amount, recovered_date: dayjs() }}>
            <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
              <InputNumber min={0.01} step={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="recovered_date" label="Recovered Date" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Outstanding before: ₹{recoveryOpen.outstanding_principal.toFixed(2)}
            </Text>
          </Form>
        </Drawer>
      )}
    </div>
  )
}


// ─── CANTEEN TAB ────────────────────────────────────────────────

function CanteenTab({ empFilter, empOptions }: { empFilter?: string; empOptions: any[] }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()
  const [txnType, setTxnType] = useState<string | undefined>()

  const listQ = useQuery({
    queryKey: ['canteen-txns', empFilter],
    queryFn: () => ledgerApi.listCanteen({ employee_id: empFilter }),
  })

  const createMut = useMutation({
    mutationFn: (v: any) => ledgerApi.createCanteenTxn({
      employee_id: v.employee_id,
      transaction_date: dayjs(v.transaction_date).format('YYYY-MM-DD'),
      transaction_type: v.transaction_type,
      amount: v.amount,
      coupon_count: v.coupon_count,
      rate_per_coupon: v.rate_per_coupon,
      payment_reference: v.payment_reference,
      remarks: v.remarks,
    }),
    onSuccess: () => {
      message.success('Transaction recorded')
      setCreateOpen(false); form.resetFields(); setTxnType(undefined)
      qc.invalidateQueries({ queryKey: ['canteen-txns'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const isPayment = txnType === 'PAYMENT' || txnType === 'REFUND'

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          New Transaction
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>Refresh</Button>
      </Space>

      <Table<CanteenTransaction>
        dataSource={listQ.data ?? []}
        rowKey="id"
        loading={listQ.isLoading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={[
          { title: 'Date', dataIndex: 'transaction_date',
            render: (v) => dayjs(v).format('DD MMM YY'), width: 110 },
          { title: 'Employee', dataIndex: 'employee_code', width: 110 },
          { title: 'Type', dataIndex: 'transaction_type',
            render: (v) => <Tag>{v.replace(/_/g, ' ')}</Tag>, width: 150 },
          { title: 'Coupons', dataIndex: 'coupon_count',
            render: (v) => v ?? '—', width: 80 },
          { title: 'Rate', dataIndex: 'rate_per_coupon',
            render: (v) => v != null ? `₹${v.toFixed(2)}` : '—', width: 80 },
          { title: 'Amount', dataIndex: 'amount',
            render: (v) => <Text type={v > 0 ? 'danger' : 'success'}>
              {v > 0 ? '+' : ''}₹{v.toFixed(2)}
            </Text>, width: 100 },
          { title: 'Balance', dataIndex: 'balance_after',
            render: (v) => <Text strong type={v > 0 ? 'danger' : undefined}>₹{v.toFixed(2)}</Text>, width: 110 },
          { title: 'Reference', dataIndex: 'payment_reference',
            render: (v) => v ?? '—' },
        ]}
      />

      <Drawer
        open={createOpen} title="New Canteen Transaction" width={460}
        onClose={() => { setCreateOpen(false); setTxnType(undefined) }}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="primary" loading={createMut.isPending}
              onClick={() => form.validateFields().then((v) => createMut.mutate(v))}>
              Record
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select showSearch options={empOptions}
              filterOption={(i, o) => (o?.label as string ?? '').toLowerCase().includes(i.toLowerCase())} />
          </Form.Item>
          <Form.Item name="transaction_type" label="Type" rules={[{ required: true }]}>
            <Select options={CANTEEN_TRANSACTION_TYPES.map((t) => ({ label: t.replace(/_/g, ' '), value: t }))}
              onChange={(v) => setTxnType(v)} />
          </Form.Item>
          <Form.Item name="transaction_date" label="Date" rules={[{ required: true }]} initialValue={dayjs()}>
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="amount" label={`Amount (₹) ${isPayment ? '— positive, will save as credit' : ''}`}
            rules={[{ required: true }]}>
            <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
          </Form.Item>
          {!isPayment && (
            <Space>
              <Form.Item name="coupon_count" label="Coupon Count">
                <InputNumber min={0} style={{ width: 120 }} />
              </Form.Item>
              <Form.Item name="rate_per_coupon" label="Rate / Coupon (₹)">
                <InputNumber min={0} step={0.5} style={{ width: 120 }} />
              </Form.Item>
            </Space>
          )}
          {isPayment && (
            <Form.Item name="payment_reference" label="Payment Reference">
              <Input placeholder="Receipt no. / cash / UPI ref" />
            </Form.Item>
          )}
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}


// ─── COLONY TAB ─────────────────────────────────────────────────

function ColonyTab({ empFilter, empOptions }: { empFilter?: string; empOptions: any[] }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()
  const [payOpen, setPayOpen] = useState<ColonyBill | null>(null)
  const [payForm] = Form.useForm()

  const listQ = useQuery({
    queryKey: ['colony-bills', empFilter],
    queryFn: () => ledgerApi.listBills({ employee_id: empFilter }),
  })

  const createMut = useMutation({
    mutationFn: (v: any) => ledgerApi.createBill({
      employee_id: v.employee_id,
      period_year: v.period_year,
      period_month: v.period_month,
      rent_amount: v.rent_amount ?? 0,
      electricity_amount: v.electricity_amount ?? 0,
      water_amount: v.water_amount ?? 0,
      maintenance_amount: v.maintenance_amount ?? 0,
      damage_recovery: v.damage_recovery ?? 0,
      other_charges: v.other_charges ?? 0,
      arrears_brought_forward: v.arrears_brought_forward ?? 0,
      remarks: v.remarks,
    }),
    onSuccess: (b) => {
      message.success(`Bill ${b.bill_no} created — total ₹${b.total_billed.toFixed(2)}`)
      setCreateOpen(false); form.resetFields()
      qc.invalidateQueries({ queryKey: ['colony-bills'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const payMut = useMutation({
    mutationFn: (v: any) => ledgerApi.recordBillPayment(payOpen!.id, {
      amount: v.amount, payment_reference: v.payment_reference,
    }),
    onSuccess: () => {
      message.success('Payment recorded')
      setPayOpen(null); payForm.resetFields()
      qc.invalidateQueries({ queryKey: ['colony-bills'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Payment failed'),
  })

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          New Bill
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => listQ.refetch()}>Refresh</Button>
      </Space>

      <Table<ColonyBill>
        dataSource={listQ.data ?? []}
        rowKey="id"
        loading={listQ.isLoading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={[
          { title: 'Bill #', dataIndex: 'bill_no', render: (v) => <Text code>{v}</Text>, width: 140 },
          { title: 'Employee', dataIndex: 'employee_code', width: 100 },
          { title: 'Period', key: 'period',
            render: (_, r) => `${r.period_month}/${r.period_year}`, width: 80 },
          { title: 'Rent', dataIndex: 'rent_amount', render: (v) => `₹${v.toFixed(2)}`, width: 90 },
          { title: 'Elec', dataIndex: 'electricity_amount', render: (v) => `₹${v.toFixed(2)}`, width: 80 },
          { title: 'Water', dataIndex: 'water_amount', render: (v) => `₹${v.toFixed(2)}`, width: 80 },
          { title: 'Total', dataIndex: 'total_billed',
            render: (v) => <Text strong>₹{v.toFixed(2)}</Text>, width: 100 },
          { title: 'Paid', dataIndex: 'paid_amount', render: (v) => `₹${v.toFixed(2)}`, width: 100 },
          { title: 'Outstanding', dataIndex: 'outstanding_amount',
            render: (v) => <Text strong type={v > 0 ? 'danger' : undefined}>₹{v.toFixed(2)}</Text>, width: 110 },
          { title: 'Status', dataIndex: 'status',
            render: (v: any) => <Tag color={BILL_STATUS_COLOR[v as keyof typeof BILL_STATUS_COLOR]}>{v}</Tag>, width: 100 },
          {
            title: 'Action', key: 'action', width: 100,
            render: (_, r) => (r.status === 'OPEN' || r.status === 'PARTIAL')
              ? <Button size="small" onClick={() => setPayOpen(r)}>Record Payment</Button>
              : null,
          },
        ]}
      />

      <Drawer
        open={createOpen} title="New Colony Bill" width={480}
        onClose={() => setCreateOpen(false)}
        footer={
          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button type="primary" loading={createMut.isPending}
              onClick={() => form.validateFields().then((v) => createMut.mutate(v))}>
              Create
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select showSearch options={empOptions}
              filterOption={(i, o) => (o?.label as string ?? '').toLowerCase().includes(i.toLowerCase())} />
          </Form.Item>
          <Space>
            <Form.Item name="period_year" label="Year" rules={[{ required: true }]} initialValue={dayjs().year()}>
              <InputNumber min={2020} max={2100} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="period_month" label="Month" rules={[{ required: true }]} initialValue={dayjs().month() + 1}>
              <InputNumber min={1} max={12} style={{ width: 100 }} />
            </Form.Item>
          </Space>
          <Form.Item name="rent_amount" label="Rent (₹)" initialValue={0}>
            <InputNumber min={0} step={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="electricity_amount" label="Electricity (₹)" initialValue={0}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="water_amount" label="Water (₹)" initialValue={0}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maintenance_amount" label="Maintenance (₹)" initialValue={0}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="damage_recovery" label="Damage Recovery (₹)" initialValue={0}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="other_charges" label="Other Charges (₹)" initialValue={0}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="arrears_brought_forward" label="Arrears (₹)" initialValue={0}>
            <InputNumber min={0} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Drawer>

      {payOpen && (
        <Drawer
          open={!!payOpen} title={`Record Payment — ${payOpen.bill_no}`} width={420}
          onClose={() => setPayOpen(null)}
          footer={
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={() => setPayOpen(null)}>Cancel</Button>
              <Button type="primary" loading={payMut.isPending}
                onClick={() => payForm.validateFields().then((v) => payMut.mutate(v))}>
                Record
              </Button>
            </Space>
          }
        >
          <Form form={payForm} layout="vertical"
            initialValues={{ amount: payOpen.outstanding_amount }}>
            <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}>
              <InputNumber min={0.01} step={50} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="payment_reference" label="Payment Reference"
              rules={[{ required: true, message: 'Required for audit' }]}>
              <Input placeholder="Receipt no. / NEFT ref / cash" />
            </Form.Item>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Outstanding: ₹{payOpen.outstanding_amount.toFixed(2)} of ₹{payOpen.total_billed.toFixed(2)}
            </Text>
          </Form>
        </Drawer>
      )}
    </div>
  )
}
